<script setup>
import {useAuthenticationStore} from '@/stores/authentication.js';
import {onMounted, watch, ref} from "vue";
import {useRouter} from "vue-router";
import {getLandingRoute} from '@/helpers/helpers.js';
import { useI18n } from 'vue-i18n';
import { useTitle } from 'vue-page-title';

const { t } = useI18n();

const { setTitle } = useTitle();
setTitle(t('home.title'));

const router = useRouter();
const authStore = useAuthenticationStore();
const isLoading = ref(true);

definePage({
  meta: {
    requiresAuth: false,
    layout: 'public',
  },
});
if (authStore.isLoggedIn) {
  router.replace(getLandingRoute(authStore.currentUser?.usergroup));
} else {
  isLoading.value = false;
}
onMounted(() => {
  if (authStore.isLoggedIn) {
    router.replace(getLandingRoute(authStore.currentUser?.usergroup));
  } else {
    isLoading.value = false;
  }
});
watch(() => authStore.isLoggedIn, (isLoggedIn) => {
  if (isLoggedIn) {
    router.replace(getLandingRoute(authStore.currentUser?.usergroup));
  }
});
</script>

<template>
  <div class="home-page">
    <div
      v-if="isLoading"
      class="d-flex justify-center align-center"
      style="min-height: 50vh;"
    >
      <v-progress-circular
        indeterminate
        color="primary"
      />
    </div>

    <div v-else>
      <v-card
        class="hero-section"
        elevation="0"
      >
        <v-card-text class="text-center pa-8">
          <div class="mb-6">
            <img
              src="@/assets/logo_large.png"
              alt="Indufast"
              class="hero-logo mb-4"
            >
          </div>

          <h1 class="text-h4 mb-4">
            {{ t('home.welcome') }}
          </h1>

          <p class="text-body-1 mb-6 text-medium-emphasis">
            {{ t('home.subtitle') }}
          </p>

          <v-btn
            color="primary"
            :to="{ path: '/login' }"
            class="px-6"
          >
            {{ t('home.login') }}
          </v-btn>
        </v-card-text>
      </v-card>

      <v-container class="py-6">
        <v-row justify="center">
          <v-col
            cols="12"
            md="8"
          >
            <h2 class="text-h5 text-center mb-6">
              {{ t('home.features') }}
            </h2>

            <v-row>
              <v-col
                cols="12"
                sm="6"
                md="6"
              >
                <v-card
                  class="feature-card h-100"
                  elevation="1"
                >
                  <v-card-text class="text-center pa-4">
                    <v-icon
                      size="48"
                      color="primary"
                      class="mb-4"
                    >
                      mdi-clipboard-account-outline
                    </v-icon>
                    <h3 class="text-h6 mb-3">
                      {{ t('home.planning.title') }}
                    </h3>
                    <p class="text-body-2">
                      {{ t('home.planning.description') }}
                    </p>
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col
                cols="12"
                sm="6"
                md="6"
              >
                <v-card
                  class="feature-card h-100"
                  elevation="1"
                >
                  <v-card-text class="text-center pa-4">
                    <v-icon
                      size="48"
                      color="primary"
                      class="mb-4"
                    >
                      mdi-clipboard-check-multiple-outline
                    </v-icon>
                    <h3 class="text-h6 mb-3">
                      {{ t('home.projects.title') }}
                    </h3>
                    <p class="text-body-2">
                      {{ t('home.projects.description') }}
                    </p>
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col
                cols="12"
                sm="6"
                md="6"
              >
                <v-card
                  class="feature-card h-100"
                  elevation="1"
                >
                  <v-card-text class="text-center pa-4">
                    <v-icon
                      size="48"
                      color="primary"
                      class="mb-4"
                    >
                      mdi-clock-check-outline
                    </v-icon>
                    <h3 class="text-h6 mb-3">
                      {{ t('home.timeRegistration.title') }}
                    </h3>
                    <p class="text-body-2">
                      {{ t('home.timeRegistration.description') }}
                    </p>
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col
                cols="12"
                sm="6"
                md="6"
              >
                <v-card
                  class="feature-card h-100"
                  elevation="1"
                >
                  <v-card-text class="text-center pa-4">
                    <v-icon
                      size="48"
                      color="primary"
                      class="mb-4"
                    >
                      mdi-account-group-outline
                    </v-icon>
                    <h3 class="text-h6 mb-3">
                      {{ t('home.employees.title') }}
                    </h3>
                    <p class="text-body-2">
                      {{ t('home.employees.description') }}
                    </p>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-container>
    </div>
  </div>
</template>

<style scoped>
.home-page {
  min-height: 100vh;
  background-color: #fafafa;
}

.hero-section {
  background: transparent;
}

.hero-logo {
  max-height: 100px;
  max-width: 250px;
}

@media (max-width: 600px) {
  .hero-logo {
    max-height: 80px;
    max-width: 200px;
  }
}
</style>
